import { Controller } from "@hotwired/stimulus"
import mapboxgl from 'mapbox-gl'
import {
  exposePdfExportMapReadiness,
  prepareMapForPdfExport
} from "../lib/pdf_export_helpers.mjs"

// Connects to data-controller="provider-search-map"
// Adds an interactive map view to provider search results
export default class extends Controller {
  static targets = ["map", "mapContainer", "resultsContainer", "expandIcon", "shrinkIcon"]
  static values = {
    accessToken: String,
    providers: Array
  }

  connect() {
    this.map = null
    this.markers = new Map() // npi -> marker
    this.locationGroups = new Map() // "lat,lng" -> [{ provider, index }]
    this.highlightedNpis = []
    this.expanded = false
    this.mapInitializationFrame = null
    this.mapInitialization = new Promise((resolve) => {
      this.resolveMapInitialization = resolve
    })
    this.removePdfExportMapReadiness = exposePdfExportMapReadiness(
      this.element,
      () => prepareMapForPdfExport(this.mapInitialization)
    )

    // Initialize map automatically if we have providers
    if (this.providersValue.length > 0) {
      // Small delay to ensure container is visible and sized
      this.mapInitializationFrame = requestAnimationFrame(() => {
        this.mapInitializationFrame = null
        this.initializeMap()
      })
    } else {
      this.resolveMapInitialization(null)
    }
  }

  initializeMap() {
    mapboxgl.accessToken = this.accessTokenValue

    try {
      this.map = new mapboxgl.Map({
        container: this.mapTarget,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: this.calculateCenter(),
        zoom: 10,
        attributionControl: false,
        // Keep the WebGL buffer so the map can be snapshotted into the PDF export.
        preserveDrawingBuffer: true
      })
      this.map.addControl(new mapboxgl.NavigationControl(), 'top-left')

      this.map.on('load', () => {
        this.addMarkers()
        this.fitBoundsToProviders()
      })

      this.resolveMapInitialization(this.map)
      this.trackMetric("mapbox.search_map.load.success")
    } catch (error) {
      this.resolveMapInitialization(null)
      this.trackMetric("mapbox.search_map.load.error")
      console.error("Failed to initialize map:", error)
    }
  }

  addMarkers() {
    // Group providers by location (rounded to 4 decimal places ≈ 11m precision)
    this.locationGroups = new Map()

    this.providersValue.forEach((provider, index) => {
      if (!provider.latitude || !provider.longitude) return
      if (provider.any_geo_location) return

      const key = `${Number(provider.latitude).toFixed(4)},${Number(provider.longitude).toFixed(4)}`
      if (!this.locationGroups.has(key)) {
        this.locationGroups.set(key, [])
      }
      this.locationGroups.get(key).push({ provider, index })
    })

    // Create markers for each location group
    this.locationGroups.forEach((group) => {
      if (group.length === 1) {
        // Single provider — numbered marker
        this.createSingleMarker(group[0].provider, group[0].index)
      } else {
        // Multiple providers at same location — stacked numbered markers
        // Iterate in reverse so the first result ends up on top (highest z-index)
        for (let i = group.length - 1; i >= 0; i--) {
          const offset = [i * 4, i * 4]
          this.createSingleMarker(group[i].provider, group[i].index, offset)
        }
      }
    })
  }

  createSingleMarker(provider, index, offset = null) {
    const el = document.createElement('div')
    el.className = 'provider-map-marker'
    el.dataset.npi = provider.npi
    el.textContent = index + 1

    const markerOptions = offset ? { offset } : {}
    const marker = new mapboxgl.Marker(el, markerOptions)
      .setLngLat([provider.longitude, provider.latitude])
      .addTo(this.map)

    el.addEventListener('click', (e) => {
      e.stopPropagation()
      this.highlightProviders([provider.npi])
    })

    // Marker hover → subtle card highlight
    el.addEventListener('mouseenter', () => {
      const card = this.element.querySelector(`[data-npi="${provider.npi}"]`)
      if (card) card.classList.add('provider-card-hover-highlighted')
    })
    el.addEventListener('mouseleave', () => {
      const card = this.element.querySelector(`[data-npi="${provider.npi}"]`)
      if (card) card.classList.remove('provider-card-hover-highlighted')
    })

    this.markers.set(provider.npi, marker)
  }

  // --- Highlight system ---

  clearHighlights() {
    this.element.querySelectorAll('.provider-card-highlighted').forEach(el => {
      el.classList.remove('provider-card-highlighted')
    })
    this.element.querySelectorAll('.provider-card-hover-highlighted').forEach(el => {
      el.classList.remove('provider-card-hover-highlighted')
    })
    this.element.querySelectorAll('.marker-highlighted').forEach(el => {
      el.classList.remove('marker-highlighted')
    })
    this.element.querySelectorAll('.marker-hover-highlighted').forEach(el => {
      el.classList.remove('marker-hover-highlighted')
    })
    this.highlightedNpis = []
  }

  highlightProviders(npis) {
    this.clearHighlights()
    this.highlightedNpis = npis

    npis.forEach(npi => {
      const card = this.element.querySelector(`[data-npi="${npi}"]`)
      const marker = this.markers.get(npi)

      if (card) card.classList.add('provider-card-highlighted')
      if (marker) marker.getElement().classList.add('marker-highlighted')
    })

    // Scroll to the first highlighted card
    const firstCard = this.element.querySelector(`[data-npi="${npis[0]}"]`)
    if (firstCard) {
      firstCard.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  // --- Card interactions ---

  cardClicked(event) {
    // Don't handle clicks on interactive elements (buttons, links) within the card
    if (event.target.closest('a, button')) return

    const detailsUrl = event.currentTarget.dataset.detailsUrl
    if (detailsUrl) {
      const drawerEvent = new Event('drawer.open')
      drawerEvent.url = detailsUrl
      document.dispatchEvent(drawerEvent)
    }
  }

  cardHovered(event) {
    const npi = event.currentTarget.dataset.npi
    if (!npi) return

    const marker = this.markers.get(npi)
    if (marker) {
      marker.getElement().classList.add('marker-hover-highlighted')
    }
  }

  cardUnhovered(event) {
    const npi = event.currentTarget.dataset.npi
    if (!npi) return

    const marker = this.markers.get(npi)
    if (marker) {
      marker.getElement().classList.remove('marker-hover-highlighted')
    }
  }

  // --- Expand / Collapse ---

  toggleExpand() {
    if (this.expanded) {
      this.collapseView()
    } else {
      this.expandView()
    }
  }

  expandView() {
    this.expanded = true

    document.dispatchEvent(new CustomEvent('drawer.expand'))

    // Switch layout from vertical to horizontal
    const layout = this.element.querySelector('.map-view-layout')
    if (layout) {
      layout.classList.remove('flex-col')
      layout.classList.add('flex-row', 'map-view-expanded')
    }

    if (this.hasExpandIconTarget) {
      this.expandIconTarget.classList.add('hidden')
    }
    if (this.hasShrinkIconTarget) {
      this.shrinkIconTarget.classList.remove('hidden')
    }

    // Resize map after drawer transition completes (drawer uses duration-500 = 500ms)
    this.resizeMapAfterTransition()
  }

  collapseView() {
    this.expanded = false

    document.dispatchEvent(new CustomEvent('drawer.collapse'))

    const layout = this.element.querySelector('.map-view-layout')
    if (layout) {
      layout.classList.add('flex-col')
      layout.classList.remove('flex-row', 'map-view-expanded')
    }

    if (this.hasExpandIconTarget) {
      this.expandIconTarget.classList.remove('hidden')
    }
    if (this.hasShrinkIconTarget) {
      this.shrinkIconTarget.classList.add('hidden')
    }

    this.resizeMapAfterTransition()
  }

  // Drawer panel uses CSS `duration-500` (500ms); wait slightly longer to resize the map
  static DRAWER_TRANSITION_MS = 550

  resizeMapAfterTransition() {
    setTimeout(() => {
      if (this.map) {
        this.map.resize()
        this.fitBoundsToProviders()
      }
    }, this.constructor.DRAWER_TRANSITION_MS)
  }

  // --- Map utilities ---

  fitBoundsToProviders() {
    const providersWithCoords = this.providersValue.filter(p => p.latitude && p.longitude && !p.any_geo_location)
    if (providersWithCoords.length === 0) return

    if (providersWithCoords.length === 1) {
      const p = providersWithCoords[0]
      this.map.flyTo({
        center: [p.longitude, p.latitude],
        zoom: 14
      })
      return
    }

    const bounds = new mapboxgl.LngLatBounds()
    providersWithCoords.forEach((provider) => {
      bounds.extend([provider.longitude, provider.latitude])
    })

    this.map.fitBounds(bounds, { padding: 50 })
  }

  calculateCenter() {
    const providers = this.providersValue.filter(p => p.latitude && p.longitude && !p.any_geo_location)
    if (providers.length === 0) return [-98.5795, 39.8283] // US center

    const avgLat = providers.reduce((sum, p) => sum + p.latitude, 0) / providers.length
    const avgLng = providers.reduce((sum, p) => sum + p.longitude, 0) / providers.length

    return [avgLng, avgLat]
  }

  trackMetric(name) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(`/api/v1/metrics/increment_counter?name=${name}`, {
      method: "POST",
      headers: csrfToken ? { "X-CSRF-Token": csrfToken } : {}
    }).catch(() => {
      // Silently ignore metric failures
    })
  }

  disconnect() {
    if (this.mapInitializationFrame != null) {
      cancelAnimationFrame(this.mapInitializationFrame)
      this.mapInitializationFrame = null
    }
    this.resolveMapInitialization?.(null)
    this.removePdfExportMapReadiness?.()
    if (this.map) {
      this.map.remove()
      this.map = null
    }
    this.markers.clear()
    this.locationGroups.clear()
  }
}
